import { useEffect, useState } from "react";
import { LuRotateCw, LuUpload } from "react-icons/lu";
import { Muted, Small } from "@/components/typography";
import { Button } from "@/components/ui/button";
import { If } from "@/components/ui/conditional";
import { backlogDetail, backlogHeadline, hasBacklog, stuckWarning, type QueueHealth } from "@/lib/queue-health";
import { useQueue } from "@/lib/queue-store";
import { useQueueState } from "@/lib/queue-banner";
import type { Page } from "@/lib/page";

/** Age and countdown have to keep moving on their own. Every other number
 * here changes when a page row changes, which re-renders Home — but a queue
 * that has genuinely stopped changes no rows at all, and that is exactly
 * when "oldest waiting 2h 14m" earns its place on the screen. */
const CLOCK_MS = 15000;

/** Device-wide upload state and a single force push, at the top of Home.
 *
 * The per-script `UploadNowButton` answers "did this one go?" next to that
 * one script. It cannot answer the question a scanner with a full phone
 * actually has — "is anything going at all?" — and once a backlog spans
 * thirty scripts, clearing it one button at a time is not a procedure
 * anyone follows. This is the other half: one line saying how much is owed
 * and how long it has been owed, the raw error when attempts keep failing,
 * and one button that lifts every hold at once. */
export function ForceUploadPanel({ health, pages }: { health: QueueHealth; pages: Page[] }) {
  const queue = useQueue();
  const { paused, pauseReason } = useQueueState();
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CLOCK_MS);
    return () => clearInterval(timer);
  }, []);

  if (!hasBacklog(health)) return null;

  const detail = backlogDetail(health, now);
  const warning = stuckWarning(health);

  async function forceAll() {
    setPushing(true);
    setPushed(null);
    try {
      setPushed((await queue?.forceUploadAll(pages)) ?? 0);
    } finally {
      setPushing(false);
    }
  }

  return (
    <div className="mb-4 space-y-2 rounded-md border px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Small className="block font-medium">{backlogHeadline(health)}</Small>
          <If when={detail}>{text => <Muted className="block text-xs">{text}</Muted>}</If>
        </div>
        <Button size="sm" disabled={!queue || pushing} onClick={() => void forceAll()}>
          <LuUpload />
          {pushing ? "Pushing…" : "Force upload all"}
        </Button>
      </div>

      <If when={warning}>
        {text => <Small className="block text-destructive">{text}</Small>}
      </If>

      {/* An auth pause is the one failure the phone cannot retry its way out
          of — the token it was built with is no longer accepted, and only a
          reload picks up a newly deployed one. */}
      <If condition={paused && pauseReason === "auth"}>
        <div className="flex items-center justify-between gap-2">
          <Small className="text-destructive">The server rejected this app's access token.</Small>
          <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
            <LuRotateCw />
            Reload app
          </Button>
        </div>
      </If>

      <If condition={pushed !== null}>
        {/* The count is only the pages that were *held back* — in backoff or
            blocked. Pages already queued need no write to move, so a small
            number here does not mean the push did little. */}
        <Muted className="block text-xs">
          {pushed === 0
            ? "Pushed the queue — nothing was being held back."
            : `Pushed the queue — released ${pushed} held-back ${pushed === 1 ? "page" : "pages"}.`}
        </Muted>
      </If>
    </div>
  );
}
